import { z } from '../zod.js';
import { NormalizedEmailSchema } from '../shared/normalizedEmail';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { hasAtLeastOneField } from '../shared/refines';

export const UpdateTicketParamsValidationSchema = z.object({
  ticketId: ObjectIdStringValidationSchema.meta({
    description: 'The ID of the ticket to update.',
  }),
});

const ticketUpdateBodyShape = {
  attendeeName: z.string().min(1).nullable().optional().meta({
    description:
      'Attendee name held on the ticket. Shown on the ticket PDF and the guest list. Send `null` to clear.',
  }),
  attendeeEmail: NormalizedEmailSchema.optional().meta({
    description:
      'Attendee email held on the ticket. Trimmed and lowercased before storage. Cannot be cleared via this endpoint. Changing it does not affect the booking or customer records.',
  }),
  metadata: z
    .array(
      z
        .object({
          key: z.string().min(1).max(50).meta({
            description: 'Metadata key. Maximum 50 characters.',
          }),
          value: z.string().max(100).nullable().meta({
            description:
              'Metadata value, maximum 100 characters. Send `null` to delete the key.',
          }),
        })
        .strict(),
    )
    .max(20)
    .optional()
    .meta({
      description:
        'Per-entry metadata operations, matching the ticket validation endpoint: each entry adds or overwrites its key, `null` deletes it, and entries not named are left untouched. A ticket holds at most 20 entries. Door staff also write metadata during check-in, so avoid replace-everything sync patterns.',
    }),
};

/**
 * The canonical list of fields a merchant may PATCH on a ticket.
 * Derived from `ticketUpdateBodyShape` so the API controller and the MCP
 * tool can iterate the same list without re-declaring it (drift-proof).
 */
export const TICKET_UPDATE_FIELDS = Object.keys(ticketUpdateBodyShape) as ReadonlyArray<
  keyof typeof ticketUpdateBodyShape
>;

export const TicketUpdateBodyValidationSchema = z
  .object(ticketUpdateBodyShape)
  .strict()
  .refine(hasAtLeastOneField, {
    message: 'At least one field must be provided.',
  });

export const UpdateTicketInputValidationSchema = z
  .object({
    ticketId: ObjectIdStringValidationSchema.meta({
      description:
        'The ID of the ticket to update. Use `list_tickets` first if you need to discover the ticket ID by attendee name, email, or ticket short ID.',
    }),
    ...ticketUpdateBodyShape,
  })
  .refine(
    (value) => {
      const { ticketId: _ticketId, ...rest } = value;
      return hasAtLeastOneField(rest);
    },
    {
      message: 'At least one field to update must be provided.',
    },
  );
